/**
 * Ranking for the ⌘K palette.
 *
 * The palette used to score every match as exactly 1:
 *
 *   filter={(value, search) => value.toLowerCase().includes(search) ? 1 : 0}
 *
 * which is a filter, not a ranking. With every survivor tied, cmdk's sort had
 * nothing to order by and results fell back to the order the groups are
 * declared in - Connectors, then Workflows, then Customers. So searching "test"
 * listed every connector whose name *or slug* merely contains those four
 * letters, and the customer actually named "test" came last.
 *
 * Grading the match fixes the ordering without loosening what matches: anything
 * that scores 0 is still filtered out exactly as before, so the palette never
 * regains cmdk's default fuzzy behaviour, which used to surface results that did
 * not contain the query at all (searching "slack" returned Google Workspace).
 *
 * The ladder is about WHERE the query lands, not how long the string is:
 *
 *   1.0  the name is exactly what was typed
 *   0.9  the slug is exactly what was typed - typing a full slug is deliberate
 *   0.8  the name starts with it
 *   0.7  it starts a word inside the name  ("Google Test" for "test")
 *   0.6  the slug starts with it
 *   0.5  it appears anywhere in the name
 *   0.4  it appears anywhere in the slug   ("latest-sync" for "test")
 *   0    no match
 *
 * The bottom rung is the one the report was about: a substring buried inside a
 * slug is the weakest possible evidence, and it was previously worth as much as
 * an exact name.
 */

/** True when `q` begins a word inside `s` - after a space, dash, underscore, dot or slash. */
function startsWord(s: string, q: string): boolean {
  let i = s.indexOf(q);
  while (i > 0) {
    if (/[\s\-_./]/.test(s[i - 1])) return true;
    i = s.indexOf(q, i + 1);
  }
  return false;
}

/**
 * Score one palette row against the query. 0 means "hide"; higher sorts first.
 *
 * @param label the display name (a connector, workflow or customer name)
 * @param hint  the secondary text, which is the slug
 */
export function searchScore(label: string, hint: string, search: string): number {
  const q = search.trim().toLowerCase();
  // cmdk only calls the filter once something has been typed, but an empty
  // query must never hide everything if it ever does.
  if (!q) return 1;

  const l = label.trim().toLowerCase();
  const h = hint.trim().toLowerCase();

  if (l === q) return 1;
  if (h === q) return 0.9;
  if (l.startsWith(q)) return 0.8;
  if (startsWord(l, q)) return 0.7;
  if (h.startsWith(q)) return 0.6;
  if (l.includes(q)) return 0.5;
  if (h.includes(q)) return 0.4;
  return 0;
}
